import { useEffect, useRef, useState, useSyncExternalStore } from 'react'

import type { DetailRow } from '@/features/dashboard/components/watchlist-wizard/watchlist-wizard-grouping'
import { WizardPriceBatcher } from '@/features/dashboard/lib/wizard-price-batcher'
import {
  type WizardPriceMap,
  wizardPriceStore,
  type WizardQuote,
} from '@/features/dashboard/lib/wizard-price-store'
import { safeParseFloat } from '@/lib/utils'
import { displayPriceOf } from '@/lib/utils/display-price'
import { useMultiIncrementalInstrumentStream } from '@/service/ws/use-incremental-instrument'
import { useMultiMarketDataStream } from '@/service/ws/use-market-data-stream'
import type { MarketDataStreamPayload } from '@/service/ws/use-web-socket-with-proto'
import { InstrumentKind, MarketDataType } from '@/types/generated/common/types'

export type { WizardPriceMap, WizardQuote }

export interface VisibleRange {
  startIndex: number
  endIndex: number
}

// Subscribing only exactly what's on screen means every scroll into new rows
// pays the full subscribe round-trip (server ack + first snapshot, ~1-2s)
// before a price appears — the buffer exists specifically to hide that: rows
// just outside the viewport get subscribed early, while the user is still
// looking at the current position, so by the time they scroll there the
// snapshot has usually already arrived. Virtuoso's own increaseViewportBy is
// a separate, smaller render-ahead optimization — this pad is intentionally
// wider than that because it's amortizing network latency, not render cost.
export const WIZARD_PRICE_RANGE_BUFFER = 30

interface WizardPricePayloads {
  tickerPayloads: MarketDataStreamPayload[]
  incrementalPayloads: MarketDataStreamPayload[]
}

// Slices the venue pane's flattened row list down to the given window,
// drops section headers, dedupes by exchange:symbol, and splits instruments
// into the two payload shapes their price actually streams on — options
// only ever get ticks via the incremental-ticker channel (see
// use-watchlist-option-quotes.ts), everything else via the plain ticker one.
const EMPTY_PAYLOADS: WizardPricePayloads = { tickerPayloads: [], incrementalPayloads: [] }

// Same windowing as `computeWizardPricePayloads` below, but returns plain
// `exchange:symbol` keys instead of WS subscribe payloads — for the
// fallback-ticker batch subscription (`useWatchlistLiveTickers`), which reads
// an already-live map rather than opening a new subscription.
export function computeVisibleRowKeys(
  rows: DetailRow[],
  range: VisibleRange,
  buffer = 0
): string[] {
  if (range.endIndex < range.startIndex) return []

  const start = Math.max(0, range.startIndex - buffer)
  const end = Math.min(rows.length - 1, range.endIndex + buffer)
  const seen = new Set<string>()
  for (let i = start; i <= end; i++) {
    const row = rows[i]
    if (!row || row.kind !== 'instrument') continue
    seen.add(`${row.instrument.exchange}:${row.instrument.symbol}`)
  }
  return [...seen]
}

export function computeWizardPricePayloads(
  rows: DetailRow[],
  range: VisibleRange,
  buffer = 0
): WizardPricePayloads {
  // endIndex < startIndex is this module's "no range reported yet" sentinel
  // (see the initial state in watchlist-detail-panel.tsx) — must be checked
  // before padding by `buffer`, otherwise e.g. {0, -1} + a 30-row buffer
  // would silently manufacture a fake "rows 0-29" guess out of a range that
  // was never actually reported as visible.
  if (range.endIndex < range.startIndex) return EMPTY_PAYLOADS

  const start = Math.max(0, range.startIndex - buffer)
  const end = Math.min(rows.length - 1, range.endIndex + buffer)
  const seen = new Set<string>()
  const tickerPayloads: MarketDataStreamPayload[] = []
  const incrementalPayloads: MarketDataStreamPayload[] = []
  for (let i = start; i <= end; i++) {
    const row = rows[i]
    if (!row || row.kind !== 'instrument') continue
    const { instrument } = row
    const key = `${instrument.exchange}:${instrument.symbol}`
    if (seen.has(key)) continue
    seen.add(key)
    if (instrument.kind === InstrumentKind.INSTRUMENT_KIND_OPTION) {
      incrementalPayloads.push(buildIncrementalPayload(instrument.symbol, instrument.exchange))
    } else {
      tickerPayloads.push(buildTickerPayload(instrument.symbol, instrument.exchange))
    }
  }
  return { tickerPayloads, incrementalPayloads }
}

// Splits price payloads into two priority tiers instead of one combined
// (visible + buffer) set. A single oversized subscribe — visible rows plus
// a wide scroll-ahead pad — risks the server coalescing one response for
// the whole batch, so the rows actually on screen wait behind symbols the
// user hasn't scrolled to yet. Subscribing `visible` and `buffered`
// separately (two independent subscribe calls, wired to two independent
// WizardPriceBatcher sessions in the hook below) means the small, currently
// visible set gets its own unblocked round trip, while the wider pad still
// warms up in the background for whenever the user actually scrolls there.
export function splitWizardPricePayloadTiers(
  rows: DetailRow[],
  range: VisibleRange,
  buffer: number = WIZARD_PRICE_RANGE_BUFFER
): { visible: WizardPricePayloads; buffered: WizardPricePayloads } {
  const visible = computeWizardPricePayloads(rows, range, 0)
  const padded = computeWizardPricePayloads(rows, range, buffer)

  const visibleKeys = new Set([
    ...visible.tickerPayloads.map((p) => `${p.exchange}:${p.symbol}`),
    ...visible.incrementalPayloads.map((p) => `${p.exchange}:${p.symbol}`),
  ])

  return {
    visible,
    buffered: {
      tickerPayloads: padded.tickerPayloads.filter(
        (p) => !visibleKeys.has(`${p.exchange}:${p.symbol}`)
      ),
      incrementalPayloads: padded.incrementalPayloads.filter(
        (p) => !visibleKeys.has(`${p.exchange}:${p.symbol}`)
      ),
    },
  }
}

interface UseWatchlistWizardPricesParams {
  tickerPayloads: MarketDataStreamPayload[]
  incrementalPayloads: MarketDataStreamPayload[]
  enabled: boolean
  /** e.g. selectedAsset.key — starts a fresh leading-flush "session" (see
   * WizardPriceBatcher) so switching assets refreshes quickly, without
   * touching the persisted price cache for other assets. */
  resetKey: string | null
}

// Prices in this dialog are reference-only, so we deliberately don't push
// every incoming tick straight into React state — that would re-render the
// whole venue pane on every message. WizardPriceBatcher (a plain,
// framework-independent class — see wizard-price-batcher.ts) accumulates
// ticks and only calls back on a leading (first message of the session) +
// trailing (every 10s afterwards) schedule.
//
// The flushed values land in `wizardPriceStore`, a module-level singleton
// (see wizard-price-store.ts) rather than this hook's own React state —
// the detail pane unmounts whenever the dialog closes, which would wipe
// component state on every close/reopen and every asset switch loses
// whatever wasn't currently subscribed. Reading through useSyncExternalStore
// means prices survive both: reopening the dialog or flipping back to an
// asset you already viewed shows its last known prices immediately, while
// the dedicated subscription for whatever's visible now still refreshes on
// its own leading+trailing schedule.
export function useWatchlistWizardPrices({
  tickerPayloads,
  incrementalPayloads,
  enabled,
  resetKey,
}: UseWatchlistWizardPricesParams): WizardPriceMap {
  const priceMap = useSyncExternalStore(
    wizardPriceStore.subscribe,
    wizardPriceStore.getSnapshot,
    wizardPriceStore.getSnapshot
  )

  const batcherRef = useRef<WizardPriceBatcher<WizardQuote> | null>(null)
  if (!batcherRef.current) {
    batcherRef.current = new WizardPriceBatcher<WizardQuote>({
      onFlush: (updates) => wizardPriceStore.merge(updates),
    })
  }

  const tickerConnected = enabled && tickerPayloads.length > 0
  const incrementalConnected = enabled && incrementalPayloads.length > 0

  const ingestQuotes = (entries: Array<[string, WizardQuote]>) => {
    batcherRef.current?.ingest(entries)
  }

  useMultiMarketDataStream({
    connected: tickerConnected,
    payloads: tickerPayloads,
    onMessage: (data) => {
      if (!data?.tickers?.length) return
      const entries = data.tickers.flatMap((ticker): Array<[string, WizardQuote]> => {
        // No own price (an untraded back-month, say) still records the tick with an empty
        // price, so the row can show "—" instead of a skeleton that never resolves.
        const price = displayPriceOf(ticker) ?? ''
        const changePercent = ticker.priceChangePercent24h
          ? safeParseFloat(ticker.priceChangePercent24h)
          : undefined
        return [[`${ticker.exchange}:${ticker.symbol}`, { price, changePercent }]]
      })
      ingestQuotes(entries)
    },
  })

  useMultiIncrementalInstrumentStream({
    connected: incrementalConnected,
    payloads: incrementalPayloads,
    onMessage: (data) => {
      if (!data?.incrementalTickers?.length) return
      const entries = data.incrementalTickers.flatMap((ticker): Array<[string, WizardQuote]> => {
        // The option's own premium only — never indexPrice, which is the UNDERLYING. Deltas
        // often carry just an index move; falling back to it stamped the underlying price
        // onto every strike, and the store's field-wise merge keeps the last real premium
        // when a tick has none.
        const price = ticker.markPrice || ticker.lastPrice || ticker.settlementPrice
        const iv = optionIv(ticker.markIv, ticker.bidIv, ticker.askIv)
        if (!price && !iv) return []
        const changePercent = ticker.stats?.priceChangePercent
          ? safeParseFloat(ticker.stats.priceChangePercent)
          : undefined
        return [
          [
            `${ticker.exchange}:${ticker.symbol}`,
            {
              price: price ?? '',
              changePercent,
              iv,
              underlyingPrice: ticker.underlyingPrice || ticker.indexPrice || undefined,
            },
          ],
        ]
      })
      ingestQuotes(entries)
    },
  })

  // enabled=false or resetKey (asset) change: clear the batcher's own timer
  // + pending buffer + leading-flush flag, so the next message for the new
  // asset/session flushes immediately instead of waiting on a trailing
  // timeout scheduled for a different asset. This does NOT touch
  // wizardPriceStore — the whole point is that previously-seen prices stay
  // visible across the switch. Runs in the effect body (not a cleanup) so
  // it never fires on unmount — see the unmount-only effect below.
  useEffect(() => {
    batcherRef.current?.reset()
  }, [enabled, resetKey])

  // Unmount cleanup tears down the timer/pending buffer only — the store
  // itself is module-level and deliberately outlives this component.
  useEffect(() => {
    return () => batcherRef.current?.dispose()
  }, [])

  return priceMap
}

// Mark IV, else the bid/ask mid — a quiet strike can carry quotes without a mark.
function optionIv(mark?: string, bid?: string, ask?: string): string | undefined {
  const positive = (value?: string) => {
    const n = Number(value)
    return value && Number.isFinite(n) && n > 0 ? n : undefined
  }
  const markIv = positive(mark)
  if (markIv !== undefined) return String(markIv)
  const bidIv = positive(bid)
  const askIv = positive(ask)
  if (bidIv !== undefined && askIv !== undefined) return String((bidIv + askIv) / 2)
  return undefined
}

// How long a subscribed row may go without any tick before it reads as "no market" ("—")
// instead of loading. Far-dated CME quarterlies have no quotes or trades at all, so the
// venue sends nothing; without a cutoff their skeleton never resolves. Well past the
// usual subscribe round-trip (~1-2s, see WIZARD_PRICE_RANGE_BUFFER).
export const WIZARD_NO_QUOTE_TIMEOUT_MS = 6000

/**
 * The subset of `keys` that have been subscribed for at least `timeoutMs` (while `enabled`).
 * Callers treat such a key with still no quote as having no market. A key's clock starts
 * the first time it's seen and survives scrolling away and back; `resetKey` (the asset)
 * restarts every clock.
 */
export function useNoQuoteTimeouts(
  keys: string[],
  {
    enabled,
    resetKey,
    timeoutMs = WIZARD_NO_QUOTE_TIMEOUT_MS,
  }: {
    enabled: boolean
    resetKey: string | null
    timeoutMs?: number
  }
): ReadonlySet<string> {
  const startedAtRef = useRef(new Map<string, number>())
  const [expired, setExpired] = useState<ReadonlySet<string>>(() => new Set())

  useEffect(() => {
    startedAtRef.current = new Map()
    setExpired(new Set())
  }, [resetKey])

  useEffect(() => {
    if (!enabled) return
    const startedAt = startedAtRef.current
    const now = Date.now()
    for (const key of keys) if (!startedAt.has(key)) startedAt.set(key, now)

    let timer: ReturnType<typeof setTimeout> | null = null
    const check = () => {
      const current = Date.now()
      let nextDeadline = Infinity
      const done = new Set<string>()
      for (const [key, start] of startedAt) {
        const deadline = start + timeoutMs
        if (deadline <= current) done.add(key)
        else nextDeadline = Math.min(nextDeadline, deadline)
      }
      setExpired((prev) =>
        prev.size === done.size && [...done].every((key) => prev.has(key)) ? prev : done
      )
      if (nextDeadline !== Infinity) timer = setTimeout(check, nextDeadline - current)
    }
    check()
    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [enabled, keys, timeoutMs])

  return expired
}

export function buildTickerPayload(symbol: string, exchange: MarketDataStreamPayload['exchange']) {
  return { symbol, exchange, dataType: MarketDataType.MARKET_DATA_TYPE_TICKER }
}

export function buildIncrementalPayload(
  symbol: string,
  exchange: MarketDataStreamPayload['exchange']
) {
  return { symbol, exchange, dataType: MarketDataType.MARKET_DATA_TYPE_INCREMENTAL_TICKER }
}
